import { getConfig } from "../../config.js";
import posthog from "posthog-js";
import { clearActiveSelectionStorage } from "./activeSelectionStorage";

// Error codes the server sends when the active org/view header no longer resolves —
// a stale client-side selection (deleted org/view, revoked access, DB reset) rather
// than a real permission failure. See handling in requestInternal below.
const STALE_SELECTION_ERROR_CODES = new Set(['ORG_NOT_MEMBER', 'VIEW_NOT_FOUND', 'VIEW_NOT_ACCESSIBLE']);

export class ApiError extends Error {
  constructor(message: string, public status?: number) {
    super(message);
    this.name = 'ApiError';
  }
}

class HttpClient {
  private defaultHeaders: Record<string, string>;

  constructor(defaultHeaders: Record<string, string> = {}) {
    this.defaultHeaders = defaultHeaders;
  }

  setOrganizationId(id: string | null): void {
    if (id) {
      this.defaultHeaders['x-organization-id'] = id;
      delete this.defaultHeaders['x-view-id'];
    } else {
      delete this.defaultHeaders['x-organization-id'];
    }
  }

  getOrganizationId(): string | null {
    return this.defaultHeaders['x-organization-id'] ?? null;
  }

  setViewId(id: string | null): void {
    if (id) {
      this.defaultHeaders['x-view-id'] = id;
      delete this.defaultHeaders['x-organization-id'];
    } else {
      delete this.defaultHeaders['x-view-id'];
    }
  }

  getViewId(): string | null {
    return this.defaultHeaders['x-view-id'] ?? null;
  }

  private async requestInternal<T>(endpoint: string, options: RequestInit = {}, queryParams?: Record<string, string | number | boolean>, skipUnwrap: boolean = false, suppressAuthRedirect: boolean = false): Promise<T> {
    const controller = new AbortController();
    const config = getConfig();
    const timeoutId = setTimeout(() => controller.abort(), config.get('api.timeout'));
    const traceId = crypto.randomUUID();

    try {
      // Build query string from params
      const queryString = queryParams ? '?' + new URLSearchParams(
        Object.entries(queryParams).map(([key, value]) => [key, String(value)])
      ).toString() : '';

      const url = `${getConfig().get('api.baseUrl')}${endpoint}${queryString}`;

      const config: RequestInit = {
        ...options,
        signal: controller.signal,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Trace-ID': traceId,
          ...this.defaultHeaders,
          ...options.headers,
        },
      };

      const response = await fetch(url, config);
      clearTimeout(timeoutId);

      if (!response.ok) {
        // Handle 401 Unauthorized - redirect to token expired page
        if (response.status === 401 && !suppressAuthRedirect) {
          window.location.href = '/auth/expired';
        }

        let errorBody: unknown;
        if (response.status !== 204 && response.status !== 205) {
          try {
            const text = await response.text();
            errorBody = text ? JSON.parse(text) : undefined;
          } catch {
            errorBody = undefined;
          }
        }

        const errorCode = errorBody && typeof errorBody === 'object' && 'code' in errorBody
          ? (errorBody as { code?: unknown }).code
          : undefined;

        // A stale org/view selection cached client-side (e.g. the org/view was deleted,
        // access was revoked, or a dev DB reset changed ids) would otherwise fail every
        // request with no way out. Clear it and reload so the app re-resolves to a
        // valid default instead.
        if (typeof errorCode === 'string' && STALE_SELECTION_ERROR_CODES.has(errorCode)) {
          clearActiveSelectionStorage();
          this.setOrganizationId(null);
          this.setViewId(null);
          window.location.reload();
        }

        const errorMessage = errorBody && typeof errorBody === 'object' && typeof (errorBody as { message?: unknown }).message === 'string'
          ? (errorBody as { message: string }).message
          : `API request failed: ${response.statusText}`;

        throw new ApiError(errorMessage, response.status);
      }

      let json: unknown;
      if (response.status !== 204 && response.status !== 205) {
        const responseText = await response.text();
        json = responseText ? JSON.parse(responseText) : undefined;
      }

      // Skip unwrapping if requested (for raw responses)
      if (skipUnwrap) {
        return json as T;
      }

      // Automatically unwrap responses with { success: true, data: ... } pattern
      if (json && typeof json === 'object' && 'success' in json && 'data' in json) {
        if (!json.success) {
          throw new ApiError('API returned success: false');
        }
        return json.data as T;
      }

      return json as T;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof ApiError) {
        if (import.meta.env.MODE !== 'development' && error.status && error.status >= 500) {
          posthog.captureException(error, { endpoint, http_status: error.status, error_type: 'api_error', trace_id: traceId });
        }
        throw error;
      }

      if (error instanceof DOMException && error.name === 'AbortError') {
        const timeoutErr = new ApiError('Request timeout');
        if (import.meta.env.MODE !== 'development') {
          posthog.captureException(timeoutErr, { endpoint, error_type: 'timeout', trace_id: traceId });
        }
        throw timeoutErr;
      }

      const networkErr = new ApiError(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      if (import.meta.env.MODE !== 'development') {
        posthog.captureException(networkErr, { endpoint, error_type: 'network_error', trace_id: traceId });
      }
      throw networkErr;
    }
  }

  async request<T>(endpoint: string, options: RequestInit = {}, queryParams?: Record<string, string | number | boolean>): Promise<T> {
    return this.requestInternal<T>(endpoint, options, queryParams, false);
  }

  async get<T>(endpoint: string, queryParams?: Record<string, string | number | boolean>, headers: Record<string, string> = {}): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'GET',
      headers
    }, queryParams);
  }

  async getRaw<T>(endpoint: string, queryParams?: Record<string, string | number | boolean>, headers: Record<string, string> = {}): Promise<T> {
    return this.requestInternal<T>(endpoint, {
      method: 'GET',
      headers
    }, queryParams, true);
  }

  async getRawSilent<T>(endpoint: string, queryParams?: Record<string, string | number | boolean>, headers: Record<string, string> = {}): Promise<T> {
    return this.requestInternal<T>(endpoint, {
      method: 'GET',
      headers
    }, queryParams, true, true);
  }

  async download(endpoint: string, filename: string, queryParams?: Record<string, string | number | boolean>): Promise<void> {
    const queryString = queryParams
      ? '?' + new URLSearchParams(Object.entries(queryParams).map(([k, v]) => [k, String(v)])).toString()
      : '';
    const url = `${getConfig().get('api.baseUrl')}${endpoint}${queryString}`;
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) {
      if (response.status === 401) window.location.href = '/auth/expired';
      throw new ApiError(`Download failed: ${response.statusText}`, response.status);
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
  }

  async post<T>(endpoint: string, body?: unknown, headers: Record<string, string> = {}): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: body ? JSON.stringify(body) : undefined
    });
  }

  async postRaw<T>(endpoint: string, body?: unknown, headers: Record<string, string> = {}): Promise<T> {
    return this.requestInternal<T>(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: body ? JSON.stringify(body) : undefined
    }, undefined, true);
  }

  async patch<T>(endpoint: string, body?: unknown, headers: Record<string, string> = {}): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: body ? JSON.stringify(body) : undefined
    });
  }

  async delete<T>(endpoint: string, headers: Record<string, string> = {}): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      headers
    });
  }
}

// Initialize the API client
const apiClient = new HttpClient();

export default apiClient;